import React, { createContext, useContext, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { material3LightTheme } from '../theme/material3';

interface ThemeContextType {
  mode: 'light';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface Material3ThemeProviderProps {
  children: ReactNode;
}

/**
 * Material Design 3 Theme Provider
 *
 * Provides a cool gray-blue color scheme that's easier on the eyes
 * than pure white backgrounds. Uses Material Design 3 specifications
 * for consistent, modern UI design.
 *
 * Color Scheme:
 * - Background: #f5f7fa (soft blue-gray)
 * - Surface: #f0f3f7 (slightly cooler)
 * - Containers: Layered cool grays for visual depth
 * - Text: Rich dark grays with blue hints
 */
export const Material3ThemeProvider: React.FC<Material3ThemeProviderProps> = ({ children }) => {
  // Use Material Design 3 light theme with cool gray-blue colors
  const muiTheme = material3LightTheme;

  const contextValue: ThemeContextType = {
    mode: 'light',
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Export both names for compatibility
export const NeumorphicThemeProvider = Material3ThemeProvider;

export default Material3ThemeProvider;
